import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { base64ToString } from '../../lib/utils';

interface ExpectedEmailDocumentPreview {
  filename: string;
  payload: string;
  contentType: string;
  documentType: 'DOKUMENT' | 'ANHANG' | 'DOKUMENT_ANHANG';
}

interface ExpectedEmailPreview {
  email: string;
  name?: string;
  subject: string;
  mailBody: string;
  customerCommunicationMail?: string;
  documents?: ExpectedEmailDocumentPreview[];
}

const DokumentEmailVorschau: React.FC<{ content: string }> = ({ content }) => {
  let email: ExpectedEmailPreview | null;

  try {
    const jsonString = base64ToString(content);
    email = JSON.parse(jsonString) as ExpectedEmailPreview;
  } catch {
    return (
      <div className="text-sm text-destructive">
        Die E-Mail-Vorschau konnte nicht geladen werden.
      </div>
    );
  }

  if (!email) {
    return null;
  }

  const decodedMailBody = base64ToString(email.mailBody);

  const initials =
    email.name && email.name.trim().length > 0
      ? email.name
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase())
          .join('')
      : (email.email[0]?.toUpperCase() ?? '?');

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex items-start justify-between gap-4 border-b pb-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center rounded-full bg-muted text-muted-foreground w-10 h-10 text-sm font-semibold">
            {initials}
          </div>
          <div className="space-y-1">
            <div className="font-semibold text-base">
              {email.name || email.email}
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-semibold">Betreff:&nbsp;</span>
              {email.subject}
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-semibold">An:&nbsp;</span>
              {email.name ? `${email.name} <${email.email}>` : email.email}
            </div>

            <div className="flex flex-wrap gap-2">
              {email.documents &&
                email.documents.length > 0 &&
                email.documents.map((doc) => (
                  <Badge
                    variant="secondary"
                    className="mt-1"
                    key={doc.filename}
                  >
                    {doc.filename}
                  </Badge>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 border rounded-md overflow-hidden bg-background">
        <iframe
          title="E-Mail Vorschau"
          className="w-full h-full"
          sandbox=""
          srcDoc={decodedMailBody}
        />
      </div>
    </div>
  );
};

interface DokumentEmailVorschauDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: string;
}

export const DokumentEmailVorschauDialog: React.FC<
  DokumentEmailVorschauDialogProps
> = ({ open, onOpenChange, content }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-w-5xl h-[95vh]">
        <div className="flex flex-col h-full">
          <div className="px-6 py-4">
            <DialogHeader>
              <DialogTitle>E-Mail-Vorschau</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6 pt-2 flex-1 min-h-0">
            <DokumentEmailVorschau content={content} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
